/**
 * Real-Time Updates Task Context
 *
 * Provides real-time task updates via Firestore subscriptions.
 * Manages Firebase connection lifecycle, token fetching, and update state.
 */

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { firestoreService } from '~/services/firestore/firestoreService';
import type { RealtimeUpdate, TaskRealtimeTokenResponse } from '~/services/firestore/types';
import { get } from '~/utils/request';

const AUTO_DISCONNECT_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

/**
 * Context interface for real-time task updates
 */
interface IRealTimeUpdatesTaskContext {
  updates: RealtimeUpdate[];
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  connect: (taskId: string) => Promise<void>;
  disconnect: () => void;
}

const defaultContextValue: IRealTimeUpdatesTaskContext = {
  updates: [],
  isConnected: false,
  isLoading: false,
  error: null,
  connect: async () => {
    // No-op: default implementation
  },
  disconnect: () => {
    // No-op: default implementation
  },
};

const RealTimeUpdatesTaskContext = createContext<IRealTimeUpdatesTaskContext>(defaultContextValue);

interface RealTimeUpdatesTaskProviderProps {
  children: ReactNode;
}

/**
 * Fetch real-time token from backend API
 */
const fetchRealtimeToken = async (taskId: string): Promise<TaskRealtimeTokenResponse> => {
  return get<TaskRealtimeTokenResponse>({
    url: `/api/v1/real-time-updates/tasks/${taskId}/token`,
  });
};

/**
 * Format error message from unknown error type
 */
const formatErrorMessage = (err: unknown): string => {
  return err instanceof Error ? err.message : 'Failed to connect to real-time updates';
};

/**
 * Initialize Firestore connection and subscription
 */
const initializeFirestoreConnection = async (
  tokenResponse: TaskRealtimeTokenResponse,
  handleUpdate: (update: RealtimeUpdate) => void
): Promise<void> => {
  await firestoreService.initialize(tokenResponse.firestore_config, tokenResponse.firebase_custom_token);

  const unsubscribe = firestoreService.subscribe(
    tokenResponse.firestore_config.collection_name,
    tokenResponse.query_config,
    handleUpdate
  );

  if (!unsubscribe) {
    throw new Error('Failed to establish subscription');
  }
};

/**
 * Real-Time Updates Task Provider Component
 */
export const RealTimeUpdatesTaskProvider: React.FC<RealTimeUpdatesTaskProviderProps> = ({ children }) => {
  const [updates, setUpdates] = useState<RealtimeUpdate[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autoDisconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);

  /**
   * Clear auto-disconnect timer
   */
  const clearAutoDisconnectTimer = useCallback(() => {
    if (autoDisconnectTimerRef.current) {
      clearTimeout(autoDisconnectTimerRef.current);
      autoDisconnectTimerRef.current = null;
    }
  }, []);

  /**
   * Set up auto-disconnect timer (1 hour)
   */
  const setupAutoDisconnect = useCallback(
    (disconnectFn: () => void) => {
      clearAutoDisconnectTimer();
      autoDisconnectTimerRef.current = setTimeout(() => {
        console.log('[RealTimeUpdatesTask] Auto-disconnect timeout (1 hour)');
        disconnectFn();
      }, AUTO_DISCONNECT_TIMEOUT_MS);
    },
    [clearAutoDisconnectTimer]
  );

  /**
   * Reset connection state
   */
  const resetState = useCallback(() => {
    setIsConnected(false);
    setUpdates([]);
    setError(null);
    currentTaskIdRef.current = null;
  }, []);

  /**
   * Handle incoming real-time update
   */
  const handleUpdate = useCallback((update: RealtimeUpdate) => {
    console.log('[RealTimeUpdatesTask] Update received:', update);
    setUpdates((prev) => [...prev, update]);
  }, []);

  /**
   * Disconnect from Firestore and reset state
   */
  const disconnect = useCallback(() => {
    console.log('[RealTimeUpdatesTask] Disconnecting');

    clearAutoDisconnectTimer();
    firestoreService.disconnect();
    resetState();
  }, [clearAutoDisconnectTimer, resetState]);

  /**
   * Handle connection error
   */
  const handleConnectionError = useCallback((err: unknown) => {
    const errorMessage = formatErrorMessage(err);
    console.error('[RealTimeUpdatesTask] Connection error:', err);
    setError(errorMessage);
    setIsConnected(false);
    currentTaskIdRef.current = null;
  }, []);

  /**
   * Connect to Firestore for a specific task
   */
  const connect = useCallback(
    async (taskId: string) => {
      // Early exit if already connected to this task
      if (isConnected && currentTaskIdRef.current === taskId) {
        console.log('[RealTimeUpdatesTask] Already connected to task:', taskId);
        return;
      }

      // Early exit if currently loading this task
      if (isLoading && currentTaskIdRef.current === taskId) {
        console.log('[RealTimeUpdatesTask] Already connecting to task:', taskId);
        return;
      }

      // Disconnect if switching tasks
      if (isConnected && currentTaskIdRef.current !== taskId) {
        console.log('[RealTimeUpdatesTask] Switching tasks, disconnecting from:', currentTaskIdRef.current);
        disconnect();
      }

      // Set task ID FIRST to prevent race conditions
      currentTaskIdRef.current = taskId;
      setIsLoading(true);
      setError(null);

      try {
        console.log('[RealTimeUpdatesTask] Connecting to task:', taskId);

        const tokenResponse = await fetchRealtimeToken(taskId);
        await initializeFirestoreConnection(tokenResponse, handleUpdate);

        setIsConnected(true);
        setupAutoDisconnect(disconnect);
        console.log('[RealTimeUpdatesTask] Connected successfully');
      } catch (err) {
        handleConnectionError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [isConnected, isLoading, handleUpdate, setupAutoDisconnect, disconnect, handleConnectionError]
  );

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearAutoDisconnectTimer();
      firestoreService.disconnect();
    };
  }, [clearAutoDisconnectTimer]);

  const contextValue: IRealTimeUpdatesTaskContext = {
    updates,
    isConnected,
    isLoading,
    error,
    connect,
    disconnect,
  };

  return <RealTimeUpdatesTaskContext.Provider value={contextValue}>{children}</RealTimeUpdatesTaskContext.Provider>;
};

/**
 * Hook to access real-time updates task context
 *
 * @throws Error if used outside of RealTimeUpdatesTaskProvider
 */
export const useRealTimeUpdatesTask = (): IRealTimeUpdatesTaskContext => {
  const context = useContext(RealTimeUpdatesTaskContext);
  if (!context) {
    throw new Error('useRealTimeUpdatesTask must be used within RealTimeUpdatesTaskProvider');
  }
  return context;
};
